import type React from 'react'

import { useState } from 'react'
import { User, Mail, Phone, Loader2 } from 'lucide-react'
import { waitlistApi } from '../../utils/api'
import type { WaitlistEntry } from '../../types'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

interface WaitlistFormProps {
  onSubmit: () => void
}

export default function WaitlistForm({ onSubmit }: WaitlistFormProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isValidE164 = (raw: string) => /^\+[1-9]\d{1,14}$/.test(raw.trim())

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      if (!isValidE164(formData.phone)) {
        setError('Enter a valid phone number with country code (e.g., +2348012345678).')
        setIsLoading(false)
        return
      }

      const waitlistData: WaitlistEntry = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phone,
      }

      const response = await waitlistApi.joinWaitlist(waitlistData)

      if (response.success) {
        onSubmit()
      } else {
        const msg =
          typeof response.message === 'string'
            ? response.message
            : (response as { message?: { message?: string } })?.message
                ?.message || 'Failed to join waitlist. Please try again.'
        setError(msg)
      }
    } catch (err: unknown) {
      setError(
        (err as Error)?.message ||
          'An unexpected error occurred. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-6 w-full'>
      {/* Error Message */}
      {error && (
        <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
          {error}
        </div>
      )}

      {/* First Name and Last Name Row */}
      <div className='grid grid-rows-1 md:grid-cols-2 gap-4'>
        <div>
          <label
            htmlFor='firstName'
            className='block text-xs font-medium text-gray-900 mb-2 text-start'
          >
            First Name
          </label>
          <div className='relative'>
            <User className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
            <input
              type='text'
              id='firstName'
              name='firstName'
              value={formData.firstName}
              onChange={handleChange}
              placeholder='Jane'
              required
              disabled={isLoading}
              className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed'
            />
          </div>
        </div>

        <div>
          <label
            htmlFor='lastName'
            className='block text-xs font-medium text-gray-900 mb-2 text-start'
          >
            Last Name
          </label>
          <div className='relative'>
            <User className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
            <input
              type='text'
              id='lastName'
              name='lastName'
              value={formData.lastName}
              onChange={handleChange}
              placeholder='Doe'
              required
              disabled={isLoading}
              className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed'
            />
          </div>
        </div>
      </div>

      {/* Email and Phone Row */}
      <div className='grid grid-rows-1 md:grid-cols-2 gap-4'>
        <div>
          <label
            htmlFor='email'
            className='block text-xs font-medium text-gray-900 mb-2 text-start'
          >
            Email
          </label>
          <div className='relative'>
            <Mail className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
            <input
              type='email'
              id='email'
              name='email'
              value={formData.email}
              onChange={handleChange}
              placeholder='jane.doe@example.com'
              required
              disabled={isLoading}
              className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed'
            />
          </div>
        </div>

        <div>
          <label
            htmlFor='phone'
            className='block text-xs font-medium text-gray-900 mb-2 text-start'
          >
            Phone Number
          </label>
          <div className='relative'>
            <Phone className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
            <PhoneNumberInput
              value={formData.phone}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, phone: value }))
                if (error) setError(null)
              }}
              placeholder='Phone number'
              required
              disabled={isLoading}
              containerClassName='border-gray-300'
              inputClassName='py-0'
              leadingIcon={<Phone className='w-4 h-4' />}
            />
          </div>
          <p className='text-[11px] text-gray-500 mt-2'>
            Include the country code (e.g., +234, +1, +44) before the phone number.
          </p>
        </div>

        <button
          type='submit'
          disabled={isLoading}
          className='w-full bg-black text-white py-3 rounded-lg font-semibold text-sm hover:bg-gray-900 transition-colors mt-4 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2'
        >
          {isLoading && <Loader2 className='w-4 h-4 animate-spin' />}
          {isLoading ? 'Joining...' : 'Join Waitlist'}
        </button>
      </div>
    </form>
  )
}
